import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import analyticsService from '../services/analyticsService';

/**
 * Custom hook for automatic GA page tracking
 * Tracks page views for all routes automatically
 */
export const useGAPageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view whenever location changes
    const pageName = getPageName(location.pathname);
    analyticsService.trackPageView(pageName, location.pathname);
  }, [location]);
};

/**
 * Get a user-friendly page name from the pathname
 */
const getPageName = (pathname) => {
  // Remove leading slash and split by '/'
  const segments = pathname.replace(/^\//, '').split('/');
  
  // Handle different route patterns
  if (pathname === '/') return 'Login';
  if (pathname === '/login') return 'Login';
  if (pathname === '/dashboard') return 'Dashboard';
  if (pathname === '/dealer') return 'RBAC Dealer Home';
  if (pathname === '/landing') return 'Landing Page';
  if (pathname.startsWith('/admin/')) {
    const step = segments[1];
    switch (step) {
      case 'users': return 'Admin - User Management';
      case 'roles': return 'Admin - Role Management';
      case 'organizations': return 'Admin - Organization Management';
      case 'settings': return 'Admin - Settings';
      default: return 'Admin Portal';
    }
  }
  if (pathname.startsWith('/backend/')) {
    const step = segments[1];
    switch (step) {
      case 'bdm-cars-v2': return 'BDM Cars V2';
      case 'my-cars-v2': return 'My Cars V2';
      case 'reports': return 'Reports';
      case 'analytics': return 'Analytics Dashboard';
      case 'analytics-test': return 'Analytics Test';
      case 'finance-approval': return 'Finance Approval';
      case 'finance-approver': return 'Finance Approver';
      case 'head-finance-approver': return 'Head Finance Approver';
      case 'auctions': return 'Auctions';
      case 'auction-helper': return 'Auction Helper';
      case 'msg91-notifications': return 'MSG91 Notifications';
      case 'msg91-config': return 'MSG91 Configuration';
      case 'user-management': return 'User Management';
      case 'user-creation': return 'User Creation';
      case 'user-list': return 'User List';
      case 'edit-user': return 'Edit User';
      case 'all-users-list': return 'All Users List';
      case 'edit-all-user': return 'Edit All User';
      case 'admin-user-list': return 'Admin User List';
      case 'edit-admin-user': return 'Edit Admin User';
      case 'dealer-management': return 'Dealer Management';
      case 'role-management': return 'Role Management';
      case 'bdm-city-mapping': return 'BDM City Mapping';
      case 'database-management': return 'Database Management';
      case 'dealer-city-mapping': return 'Dealer City Mapping';
      case 'manage-drivers': return 'Manage Drivers';
      case 'manage-suppliers': return 'Manage Suppliers';
      case 'favorites': return 'Favorites';
      case 'evaluator-home': return 'Evaluator Home';
      case 'otp-verification': return 'OTP Verification';
      case 'otp-gate': return 'OTP Gate';
      case 'otp-gate-html': return 'OTP Gate HTML';
      case 'yard-evaluation-link': return 'Yard Evaluation Link';
      case 'supplier-master': return 'Supplier Master';
      case 'yard-master': return 'Yard Master';
      case 'steering-wheel-master': return 'Steering Wheel Master';
      case 'vehicle-spec-master': return 'Vehicle Spec Master';
      case 'banner-master': return 'Banner Master';
      case 'car-type-master': return 'Car Type Master';
      case 'supplier-converter': return 'Supplier Converter';
      case 'add-inventory': return 'Add Inventory';
      case 'evaluation-otp': return 'Evaluation OTP';
      case 'evaluation-yards': return 'Evaluation Yards';
      case 'evaluation-cars': return 'Evaluation Cars';
      case 'evaluation-photo-upload': return 'Evaluation Photo Upload';
      case 'evaluation': return 'Evaluation';
      case 'delivery': return 'Delivery';
      case 'deposit-refund-tracking': return 'Deposit Refund Tracking';
      case 'noc-management': return 'NOC Management';
      case 'manage-inventory': return 'Manage Inventory';
      default: return 'Backend Portal';
    }
  }
  if (pathname.startsWith('/eval/')) return 'Evaluator Portal';
  if (pathname.startsWith('/generate-yard-link')) return 'Yard Link Generator';
  if (pathname.startsWith('/manage-suppliers')) return 'Manage Suppliers';
  if (pathname.startsWith('/yard-evaluation-link')) return 'Yard Evaluation Link';
  
  // Default fallback
  return segments.map(segment => 
    segment.charAt(0).toUpperCase() + segment.slice(1)
  ).join(' - ') || 'Unknown Page';
};
